import { useEffect, useState } from "react";
import { Bot, Mail, MessageSquare, FileText, DollarSign, Calendar, Star, Phone, CheckCircle2 } from "lucide-react";

type Action = {
  icon: any;
  agent: string;
  text: string;
  amount?: string;
  color: string;
};

const ACTIONS: Action[] = [
  { icon: Mail, agent: "Inbox AI", text: "Replied to Sarah about Tuesday's appointment", color: "text-blue-500" },
  { icon: FileText, agent: "Sales AI", text: "Sent estimate to Acme Properties", amount: "$1,840", color: "text-purple-500" },
  { icon: DollarSign, agent: "Collections AI", text: "Auto-reminder sent — invoice INV-1042", color: "text-emerald-500" },
  { icon: DollarSign, agent: "Collections AI", text: "Payment received from M. Chen", amount: "+$425", color: "text-emerald-500" },
  { icon: Calendar, agent: "Scheduler AI", text: "Booked detail job for Friday 2pm", color: "text-amber-500" },
  { icon: MessageSquare, agent: "Inbox AI", text: "Answered SMS lead from Google Ads", color: "text-blue-500" },
  { icon: Star, agent: "Reviews AI", text: "Requested Google review from happy client", color: "text-yellow-500" },
  { icon: FileText, agent: "Books AI", text: "Categorized 8 expenses, reconciled bank feed", color: "text-pink-500" },
  { icon: Phone, agent: "Voice AI", text: "Answered missed call, captured lead details", color: "text-cyan-500" },
  { icon: DollarSign, agent: "Collections AI", text: "Paid invoice closed — $2,100", amount: "+$2,100", color: "text-emerald-500" },
];

export const LiveActivityFeed = () => {
  const [items, setItems] = useState<{ key: number; action: Action }[]>([]);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    // Seed with first 3
    const initial = [0, 1, 2].map((i) => ({ key: -i, action: ACTIONS[i] }));
    setItems(initial);
    setCounter(3);

    const interval = setInterval(() => {
      setCounter((c) => {
        const next = ACTIONS[c % ACTIONS.length];
        setItems((prev) => [{ key: c, action: next }, ...prev].slice(0, 5));
        return c + 1;
      });
    }, 2200);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-16 md:py-20 border-t border-border/50 bg-muted/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-emerald-600 dark:text-emerald-400 font-medium">Live — running right now</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              While you read this,<br />
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                AutoPilot is working.
              </span>
            </h2>
            <p className="text-muted-foreground text-lg">
              This is a real-time feed of what the AI does for users every single second.
              No clicks. No prompts. Just done.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4">
              <div>
                <div className="text-2xl font-bold">847k+</div>
                <div className="text-xs text-muted-foreground">Actions / month</div>
              </div>
              <div>
                <div className="text-2xl font-bold">$2.1M</div>
                <div className="text-xs text-muted-foreground">Auto-collected</div>
              </div>
              <div>
                <div className="text-2xl font-bold">23 hrs</div>
                <div className="text-xs text-muted-foreground">Saved / user / wk</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-2xl" />
            <div className="relative rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/40">
                <div className="flex items-center gap-2">
                  <Bot className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">AutoPilot Activity</span>
                </div>
                <span className="text-xs text-muted-foreground">Last 5 actions</span>
              </div>
              <div className="divide-y divide-border max-h-[360px] overflow-hidden">
                {items.map(({ key, action }, i) => {
                  const Icon = action.icon;
                  return (
                    <div
                      key={key}
                      className={`flex items-start gap-3 px-4 py-3.5 ${i === 0 ? "animate-fade-in bg-primary/5" : ""}`}
                    >
                      <div className={`shrink-0 w-9 h-9 rounded-lg bg-muted flex items-center justify-center ${action.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-0.5">
                          <span className="font-medium">{action.agent}</span>
                          <span>•</span>
                          <span>just now</span>
                        </div>
                        <div className="text-sm flex items-center gap-2">
                          <span className="truncate">{action.text}</span>
                          {action.amount && (
                            <span className="ml-auto text-emerald-500 font-semibold whitespace-nowrap">
                              {action.amount}
                            </span>
                          )}
                        </div>
                      </div>
                      {i === 0 && <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-1" />}
                    </div>
                  );
                })}
              </div>
              <div className="px-4 py-2.5 border-t border-border bg-muted/40 text-xs text-muted-foreground text-center">
                You'd be doing all this manually. Don't.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveActivityFeed;
